import React, { useState, useEffect, useRef } from "react";
import { Modal, Input } from "antd";
import { esign_verification_and_log } from '@/utils/api';
import { toast } from "react-toastify";
import { CheckCircle, Lock, Shield, User, X, ShieldCheck } from "lucide-react";
import useAppSetting from '@/hooks/useAppSetting';
import { ESIGN_REQUIRED_SETTING, ESIGN_BYPASS_USER, ESIGN_BYPASS_REASON } from '@/utils/esignSetting';

const ESignAuth = ({ visible, onCancel, onSubmit, disabled = false, req }) => {
  const esignRequired = useAppSetting(ESIGN_REQUIRED_SETTING, true);
  const bypassedRef = useRef(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationError, setVerificationError] = useState(null);
  const [values, setValues] = useState({
    comment: "",
    username: "",
    password: "",
  });

  // Global "E-Signature Required" is OFF — auto-complete instead of prompting for credentials.
  useEffect(() => {
    if (visible && !esignRequired && !bypassedRef.current) {
      bypassedRef.current = true;
      onSubmit({ comment: ESIGN_BYPASS_REASON, username: ESIGN_BYPASS_USER, password: null });
    }
    if (!visible) bypassedRef.current = false;
  }, [visible, esignRequired, onSubmit]);

  const setField = (field, value) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    if (verificationError) setVerificationError(null);
  };

  const resetForm = () => {
    setValues({ comment: "", username: "", password: "" });
    setVerificationError(null);
  };

  const handleSubmit = () => {
    const { username, password, comment } = values;
    if (!username || !password || !comment) {
      setVerificationError("All fields are required.");
      return;
    }
    if (comment.trim().length < 10) {
      setVerificationError("Remarks must be at least 10 characters.");
      return;
    }
    setIsVerifying(true);
    esign_verification_and_log(
      { username, password, comment, ...(req && req) },
      (res) => {
        setIsVerifying(false);
        if (res?.is_user_verified) {
          toast.success("Credentials verified successfully", { autoClose: 1500 });
          onSubmit(values);
          resetForm();
        } else {
          setVerificationError("Invalid username or password");
          toast.error("Invalid username or password. Please try again.");
        }
      }
    );
  };

  const handleCancel = () => {
    resetForm();
    onCancel();
  };

  const commentInvalid = values.comment.length > 0 && values.comment.trim().length < 10;
  const canSubmit = !isVerifying && !disabled && values.username && values.password && values.comment && !commentInvalid;

  return (
    <Modal
      open={visible && esignRequired}
      onCancel={!isVerifying ? handleCancel : undefined}
      footer={null}
      centered
      width={500}
      closable={false}
      destroyOnHidden
      zIndex={1600}
      styles={{
        content: { padding: 0, overflow: "hidden", borderRadius: "20px" },
        mask: { backdropFilter: "blur(4px)" },
      }}
    >
      {/* Header */}
      <div className="bg-[#204448] px-8 pt-7 pb-6 relative">
        <button
          onClick={handleCancel}
          disabled={isVerifying}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white/60 hover:text-white transition-all"
        >
          <X size={15} />
        </button>

        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-white/10 border border-white/20 flex items-center justify-center flex-shrink-0">
            <ShieldCheck size={28} className="text-green-400" />
          </div>
          <div>
            <h2 className="text-white text-lg font-bold m-0 leading-tight">
              E-Signature Verification
            </h2>
            {req?.batch_number && (
              <p className="text-white/50 text-xs mt-0.5 m-0">
                Batch <span className="text-white/80 font-semibold">{req.batch_number}</span>
                {req?.action_type && (
                  <> · <span className="text-white/80 font-semibold">{req.action_type}</span></>
                )}
              </p>
            )}
          </div>
        </div>

        {/* 21 CFR badge */}
        <div className="mt-5 flex bg-white/10 rounded-xl p-1">
          <div className="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg bg-white text-[#204448] shadow-sm text-xs font-semibold">
            <Shield size={13} />
            21 CFR Part 11 — Password Auth
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="bg-white px-8 py-6">
        {/* Error banner */}
        {verificationError && (
          <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-5">
            <span className="text-red-500 text-base flex-shrink-0 mt-0.5">⚠️</span>
            <p className="text-red-700 text-sm m-0">{verificationError}</p>
          </div>
        )}

        <div className="space-y-4">
          {/* Username */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">
              Username <span className="text-red-500">*</span>
            </label>
            <Input
              prefix={<User size={14} className="text-gray-400" />}
              placeholder="Enter your username"
              value={values.username}
              onChange={(e) => setField("username", e.target.value)}
              disabled={isVerifying || disabled}
              size="large"
            />
          </div>

          {/* Password */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">
              Password <span className="text-red-500">*</span>
            </label>
            <Input.Password
              prefix={<Lock size={14} className="text-gray-400" />}
              placeholder="Enter your password"
              value={values.password}
              onChange={(e) => setField("password", e.target.value)}
              disabled={isVerifying || disabled}
              size="large"
            />
          </div>

          {/* Remarks */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">
              Remarks <span className="text-red-500">*</span>
            </label>
            <Input.TextArea
              placeholder="Provide a reason for this action (min. 10 characters)"
              value={values.comment}
              onChange={(e) => setField("comment", e.target.value)}
              disabled={isVerifying || disabled}
              rows={3}
              maxLength={500}
              showCount
              status={commentInvalid ? "error" : ""}
            />
            {commentInvalid && (
              <p className="text-red-500 text-xs mt-1">Minimum 10 characters required.</p>
            )}
          </div>

          {/* Submit */}
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="w-full py-3 rounded-xl bg-[#204448] hover:bg-[#1a3538] disabled:bg-gray-200 disabled:text-gray-400 text-white font-bold text-sm transition-colors flex items-center justify-center gap-2"
          >
            {isVerifying ? (
              <><span className="animate-spin">⏳</span> Verifying…</>
            ) : (
              <><CheckCircle size={15} /> Verify & Sign</>
            )}
          </button>
        </div>

        {/* Cancel */}
        <button
          onClick={handleCancel}
          disabled={isVerifying}
          className="w-full mt-3 py-2.5 rounded-xl border border-gray-200 text-gray-500 text-sm font-medium hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
      </div>
    </Modal>
  );
};

export default ESignAuth;
